import { Component, Inject, Input } from '@angular/core';
import { HttpErrorResponse } from "@angular/common/http";

import { Subject, forkJoin, of } from 'rxjs';
import { filter, mergeMap } from 'rxjs/operators';

import { EventBusService, Events } from '../../../../technical-modules/events/services/event-bus.service';

import { AccesService } from '../services/acces.service';
import { AccesDto } from './acces-operation-binder.component';
import { OuvrageAccesDTO } from '../entities/ouvrage-acces-dto.entity';
import { OuvrageDto } from '../../../structure/ouvrage/entities/ouvrage-dto.entity';
import { AccesOperationBinderEntity } from '../entities/acces-operation-binder.entity';
import { AccesOperation } from '../entities/acces-operation.entity';
import { AccesBindedElement } from '../entities/acces-binded-element';
import { ParcService } from '../../../structure/parc/services/parc.service';

@Component({
	selector: 'acces-association-operations-dialog',
	templateUrl: 'acces-association-operations-dialog.html',
	styleUrls: ['acces-association-operations-dialog.css'],
})
export class AccesAssociationOperationsDialogComponent {
	// Gestion globale de la fenetre
	displaySubject = new Subject<boolean>();

	// Donnees a lier sur la vue
	loaded = false;
	
	acces!: AccesDto;
	ouvrages!: OuvrageAccesDTO[];
	accesOperationBinder!: AccesOperationBinderEntity;
	shouldDisableButtons: boolean = false;
	hasSomeActes: boolean = false;

	constructor(
		@Inject(AccesService) private accesService: AccesService,
		@Inject(EventBusService) private eventBusService: EventBusService,
		@Inject(ParcService) private parcService: ParcService
	) {
		let displayCommandsSubject = new Subject<number>();

		// A la reception d'une demande d'affichage
		// Charger l'acces correspondant
		let acces$ = displayCommandsSubject.pipe(
			mergeMap(
				(accesId) => {
					this.accesOperationBinder = new AccesOperationBinderEntity();
					this.accesOperationBinder.accesId = accesId;
					return this.accesService.getById(accesId);
				}
			)
		);

		// Une fois l'acces obtenu, charger toutes les donnees complementaires
		let accesData$ = acces$.pipe(
			mergeMap(
				(acces) => {
					let ouvrages$ = accesService.getOuvrageDTOByAcces(acces.id);
					let pendingOperations$ = accesService.getPendingOperationsForAcces(acces.id);
					let parc$ = this.parcService.getParcTLRById(acces.parcId);

					return forkJoin({
						acces: of(acces),
						parc: parc$,
						ouvrages: ouvrages$,
						pendingOperations: pendingOperations$
					});
				}
			)
		);

		// Uniquement quand tous les elements sont charges, on bind les donnees sur la vue
		accesData$.subscribe(
			(data) => {
				let accesData = data;
				let accesDto = new AccesDto();
				accesDto.id = accesData.acces.id;
				accesDto.numero = accesData.acces.numero;
				accesDto.type = accesData.acces.typeAcces;
				accesDto.accesCegex = accesData.acces.accesCegex;
				accesDto.dateDebut = accesData.acces.dateDebut;
				accesDto.pendingOperations = accesData.pendingOperations;
				accesDto.ice = accesData.acces.ice;
				this.accesOperationBinder.aucunActeAssociable = accesData.acces.aucunActeAssociable;
				accesDto.ouvragesDto = accesData.ouvrages.map( o => {
					let ouvrageDto = new OuvrageDto();
					ouvrageDto.id = o.id;
					ouvrageDto.nom = o.nom;
					ouvrageDto.idClasse = o.classeOuvrage;
					return ouvrageDto;
				} );
				accesDto.parc = data.parc;
				this.acces = accesDto;
				this.ouvrages = accesData.ouvrages.sort((a, b) => a.nom.localeCompare(b.nom));
				this.loaded = true;
				// on peut ouvrir le popup
				this.displaySubject.next(true);
			}
		)

		// Quand on masque la fenetre de dialogue, on nettoie les donnees passees
		this.displaySubject.pipe(
			filter(show => !show)
		).subscribe(
			(_show) => {
				this.loaded = false;
			}
		)
		
		// Quand l'evenement de requete d'affichage arrive sur le bus general, lancer la commande
		eventBusService.observe(Events.AssociationOperationsModalRequested, id => {
			console.log('reçu une demande d\'association pour l\'acces: ', id);
			displayCommandsSubject.next(id);
		});
	}
	
	@Input() id = 'accesAssociationOperationsDialog';
	
	onBindingOperation(bindedElement: AccesBindedElement) {
		console.log('onBindingOperation, bindedElement: ', bindedElement);
		if (this.accesOperationBinder) {
			this.accesOperationBinder.liaisonAvecOperationsExistantes = [];
			this.accesOperationBinder.creationNouvellesOperations = [];
		} else {
			this.accesOperationBinder = new AccesOperationBinderEntity();
			this.accesOperationBinder.accesId = bindedElement.accesId;
		}
		Object.keys(bindedElement.acteOperations).map($ => Number($)).forEach( acteId => {
			if (acteId === -1) {
				bindedElement.acteOperations[acteId].forEach( (accesOperation: AccesOperation) => {
					// operations existantes sans actes à associer à cet acces 
					console.log('operation existante sans acteOnm à binder: ', accesOperation);
					this.accesOperationBinder.liaisonAvecOperationsExistantes.push(accesOperation);
				} );
			} else {
				bindedElement.acteOperations[acteId].forEach( (accesOperation: AccesOperation) => {
					if (accesOperation.createdByAurora) {
						// operation à creer dans CARL puis à binder à cet acces
						console.log('operation non existante de type acteOnmId: ', accesOperation.acteOnmId, ' à creer: ', accesOperation);
						this.accesOperationBinder.creationNouvellesOperations.push(accesOperation);
					} else {
						// operation existante à binder dans CARL
						console.log('operation existante de type acteOnmId: ', accesOperation.acteOnmId, ' à binder: ', accesOperation);
						this.accesOperationBinder.liaisonAvecOperationsExistantes.push(accesOperation);
					}
				} );
			}
		} );
	}
	
	onAfterChargementActes(hasSomeActes: boolean) {
		console.log('onAfterChargementActes, hasSomeActes: ', hasSomeActes);
		this.hasSomeActes = hasSomeActes;
	}
	
	onBindingWorkOrder(bindedElement: AccesBindedElement) {
		console.log('onBindingWorkOrder, bindedElement: ', bindedElement);
		if (this.accesOperationBinder) {
			this.accesOperationBinder.liaisonAvecWorkOrders = [];
		} else {
			this.accesOperationBinder = new AccesOperationBinderEntity();
			this.accesOperationBinder.accesId = bindedElement.accesId;
		}
		this.accesOperationBinder.liaisonAvecWorkOrders = bindedElement.workOrders;
	}

	onBindingNotification(bindedElement: AccesBindedElement) {
		console.log('onBindingNotification, bindedElement: ', bindedElement);
		if (this.accesOperationBinder) {
			this.accesOperationBinder.liaisonAvecNotifications = [];
		} else {
			this.accesOperationBinder = new AccesOperationBinderEntity();
			this.accesOperationBinder.accesId = bindedElement.accesId;
		}
		this.accesOperationBinder.liaisonAvecNotifications = bindedElement.notifications;
	}
	
	terminer() {
		// terminer l'association
		console.log('utilisateur a fini de remplir sa demande association');
		// verifications avant soumission
		let accesOuvragesIds = this.ouvrages != null ? this.ouvrages.map($ => $.id) : [];
		let operationOuvrageIds = new Set();
		this.accesOperationBinder.creationNouvellesOperations.forEach( op => operationOuvrageIds.add(op.ouvrageId) );
		this.accesOperationBinder.liaisonAvecOperationsExistantes.forEach( op => operationOuvrageIds.add(op.ouvrageId) );
		var unBindedOuvrages = accesOuvragesIds.filter(item => accesOuvragesIds.indexOf(item) < 0);
		if (this.acces.accesCegex && unBindedOuvrages.length > 0) {
			alert('Vous devez choisir au moins une opération par ouvrage selectionné');
		} else {
			// appeler le serveur pour appliquer les changements
			this.shouldDisableButtons = true;
			this.accesService.associerOperations(this.accesOperationBinder).subscribe(
				(_data) => {
					this.shouldDisableButtons = false;
					// prévenir l'appelant que le traitement a fini
					this.eventBusService.emit(Events.AccesListChanged, null);
					// fermer le popup
					this.close();
				}, (error: HttpErrorResponse) => {
					this.shouldDisableButtons = false;
					console.error('Erreur rencontrée lors l\'association de l\'accès aux opérations, erreur: ', error);
					alert('Erreur rencontrée lors l\'association de l\'accès aux opérations');
				}
			);
		}
	}
	
	onBinderSelectionChange(hasSelection: boolean) {
	    if (hasSelection) {
	        this.accesOperationBinder.aucunActeAssociable = false;
	    }
	}
	
	close() {
		this.displaySubject.next(false);
	}
}